import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { adminApi, formatApiError } from '../../admin/AuthContext';
import { resolveImageUrl, formatDate } from '../../lib/api';

const AdminPosts = () => {
  const [posts, setPosts] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = () => adminApi.get('/admin/blog').then(res => setPosts(res.data.posts)).catch(err => toast.error(formatApiError(err)));
  useEffect(() => { load(); }, []);

  const togglePublish = async (post) => {
    try {
      const { data: full } = await adminApi.get(`/admin/blog/${post.id}`);
      await adminApi.put(`/admin/blog/${post.id}`, { ...full, published: !full.published });
      toast.success(full.published ? 'Post moved to drafts' : 'Post published');
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const confirmDelete = async () => {
    try {
      await adminApi.delete(`/admin/blog/${toDelete.id}`);
      toast.success('Post deleted');
      setToDelete(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blog Posts</h1>
          <p className="text-sm text-gray-500">{posts ? `${posts.length} article${posts.length === 1 ? '' : 's'}` : ''}</p>
        </div>
        <Link to="/admin/posts/new">
          <Button className="bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white" data-testid="admin-new-post"><Plus className="w-4 h-4 mr-1" /> New Post</Button>
        </Link>
      </div>

      {!posts ? (
        <div className="flex items-center gap-2 text-gray-500 py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : posts.length === 0 ? (
        <p className="text-center text-gray-500 py-20">No posts yet. Create your first article.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y" data-testid="admin-post-list">
          {posts.map(post => (
            <div key={post.id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4" data-testid={`admin-post-row-${post.slug}`}>
              <div className="w-full sm:w-28 aspect-[3/2] rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                {post.cover_image && <img src={resolveImageUrl(post.cover_image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${post.published ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`admin-post-status-${post.slug}`}>
                    {post.published ? 'Published' : 'Draft'}
                  </span>
                  <span className="text-xs text-gray-500">{post.category}</span>
                </div>
                <h3 className="font-semibold text-gray-900 truncate">{post.title}</h3>
                <p className="text-xs text-gray-500">/blog/{post.slug} · {formatDate(post.updated_at)} · {post.read_time} min read</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => togglePublish(post)} data-testid={`admin-post-toggle-${post.slug}`} title={post.published ? 'Unpublish' : 'Publish'}>
                  {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Link to={`/admin/posts/${post.id}`}>
                  <Button size="sm" variant="outline" data-testid={`admin-post-edit-${post.slug}`}><Pencil className="w-4 h-4" /></Button>
                </Link>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setToDelete(post)} data-testid={`admin-post-delete-${post.slug}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" will be permanently removed from the website.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="admin-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPosts;
